import React from "react";

import Tooltip from "antd/es/tooltip";
import cn from "classnames";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import { EntityTypeShapedIcon } from "@mapmycustomers/ui";

import Interactive from "@app/component/map/FeaturedMap/essentials/controls/Interactive";
import IMapViewContext from "@app/component/view/types/IMapViewContext";
import withMapView from "@app/scene/map/utils/withMapView";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { MAP_ENTITY_TYPES } from "@app/util/map/consts";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import styles from "./EntityVisibility.module.scss";

interface Props extends IMapViewContext {}

const EntityVisibility: React.FC<Props> = ({
  updateViewStateAndFetch,
  viewState: { visibleEntities },
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();

  const handleClick = (entityType: EntityTypesSupportedByMapsPage) => {
    analytics.clicked([`Show/hide ${getEntityTypeAnalyticsName(entityType, true, true)}`]);
    const updatedVisibleEntities = visibleEntities.includes(entityType)
      ? visibleEntities.filter((et) => et !== entityType)
      : [...visibleEntities, entityType];
    // it's ok to save even if updatedVisibleEntities length is 0
    updateViewStateAndFetch({ request: { visibleEntities: updatedVisibleEntities } });
  };

  return (
    <Interactive className={styles.container}>
      {MAP_ENTITY_TYPES.map((entityType) => (
        <Tooltip
          arrowPointAtCenter
          key={entityType}
          placement="bottom"
          title={intl.formatMessage(
            {
              id: "map.filters.entityVisibility.tooltip",
              defaultMessage:
                "{hidden, select, true {Show} other {Hide}} {entityType, select, accounts {Companies} contacts {People} deals {Deals} other {Records}}",
              description:
                "A tooltip to show when hovering over entity icons in Entity Visibility toggle on Maps",
            },
            {
              entityType,
              hidden: !visibleEntities.includes(entityType),
            }
          )}
        >
          {/* Without this div Tooltip is only shown when hovering over the svg icon */}
          <div>
            <EntityTypeShapedIcon
              className={cn(styles.icon, styles[entityType], {
                [styles.disabled]: !visibleEntities.includes(entityType),
              })}
              entityType={entityType}
              onClick={() => handleClick(entityType)}
            />
          </div>
        </Tooltip>
      ))}
    </Interactive>
  );
};

export default withMapView(EntityVisibility, ["visibleEntities"]);
